import { api, canWrite, isOwner, send } from "../api";
import { nodeStateChip } from "../parts";
import type { Enrol, NodeDetail, PeerRow } from "../types";
import { actionButton, ago, chart, confirmButton, date, dateTime, days, debounce, field, figures, flash, form, gb, geoCell, h, input, link, loadChip, meter, meters, num, pager, rate, secret, select, show, size, table, td, textarea, th, uptime, userLink, who } from "../ui";
import { cycleLine } from "./nodes";

export async function nodePage() {
  const relay = new URLSearchParams(location.search).get("relay") ?? "";
  if (!relay) return show("Node", "No node given.", link("/nodes", "← All nodes"));
  let range = "24h";
  const d0 = await api<NodeDetail>(`/v1/admin/nodes/${encodeURIComponent(relay)}?range=${range}`);
  if (!d0) return;
  const write = canWrite();
  const path = `/v1/admin/nodes/${encodeURIComponent(relay)}`;
  const chartBox = h("div");
  const rangeTabs = h("div", { class: "periods", role: "group", "aria-label": "Range" });
  const drawChart = (d: NodeDetail) => {
    const fmt = (x: NodeDetail["series"][number]) =>
      x.day ?? (range === "24h" ? new Date((x.ts ?? 0) * 1000).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : new Date((x.ts ?? 0) * 1000).toLocaleString([], { month: "short", day: "numeric", hour: "numeric" }));
    const keys = d.series.map(fmt);
    const traffic = chart(keys, [
      { name: "out", label: "sent", values: new Map(d.series.map((x) => [fmt(x), x.relay_out])) },
      { name: "in", label: "received", values: new Map(d.series.map((x) => [fmt(x), x.relay_in])) },
    ], size);
    const extra = d.series[0]?.peers !== undefined
      ? chart(keys, [{ name: relay, label: "devices connected (peak)", values: new Map(d.series.map((x) => [fmt(x), x.peers ?? 0])) }], (n) => num(Math.round(n)))
      : chart(keys, [{ name: relay, label: "accounts", values: new Map(d.series.map((x) => [fmt(x), x.accounts ?? 0])) }], (n) => num(Math.round(n)));
    const pctFmt = (n: number) => `${Math.round(n)}%`;
    const cpu = chart(keys, [{ name: "out", label: "CPU, peak", values: new Map(d.series.map((x) => [fmt(x), x.cpu_pct ?? 0])) }], pctFmt);
    const mem = chart(keys, [{ name: "in", label: "memory, peak", values: new Map(d.series.map((x) => [fmt(x), x.mem_pct ?? 0])) }], pctFmt);
    const bw = chart(keys, [{ name: relay, label: "network, peak (the busier direction)", values: new Map(d.series.map((x) => [fmt(x), ((x.net_peak_bps ?? 0) * 8) / 1e6])) }], (n) => `${n.toFixed(n < 10 ? 2 : 0)} Mbit/s`);
    const gap = () => h("div", { style: "height:10px" });
    chartBox.replaceChildren(traffic, gap(), extra, h("h3", null, "Resources"), cpu, gap(), mem, gap(), bw);
    if (!d.series.length) chartBox.append(h("p", { class: "muted" }, "No samples in this range yet."));
  };
  for (const r of ["24h", "7d", "30d", "90d"]) {
    const b = h("button", { type: "button", "aria-pressed": String(r === range) }, r === "24h" ? "24 hours" : r === "7d" ? "7 days" : r === "30d" ? "30 days" : "90 days") as HTMLButtonElement;
    b.addEventListener("click", async () => {
      range = r;
      for (const x of rangeTabs.querySelectorAll("button")) x.setAttribute("aria-pressed", String(x === b));
      const d = await api<NodeDetail>(`${path}?range=${r}`);
      if (d) drawChart(d);
    });
    rangeTabs.append(b);
  }

  // Connected now: server-side search, kind filter, sort, pages.
  const peersBox = h("div");
  const ps = { q: "", kind: "", sort: "circuits", offset: 0 };
  const kindBar = h("div", { class: "periods", role: "group", "aria-label": "Kind" });
  const search = h("input", { type: "search", placeholder: "Search account, device, peer id, IP or city", "aria-label": "Search connected devices" }) as HTMLInputElement;
  const sortSel = select({ "aria-label": "Sort" }, [["circuits", "Sort: most circuits"], ["since", "Sort: connected longest"], ["name", "Sort: name"]]);
  const loadPeers = async () => {
    const r = await api<{ total: number; limit: number; offset: number; kinds: Record<string, number>; peers: PeerRow[] }>(
      `${path}/peers?${new URLSearchParams({ q: ps.q, kind: ps.kind, sort: ps.sort, limit: "50", offset: String(ps.offset) })}`,
    );
    if (!r) return;
    const all = Object.values(r.kinds).reduce((t, n) => t + n, 0);
    kindBar.replaceChildren(
      ...[["", `All ${num(all)}`], ["host", `Computers ${num(r.kinds.host ?? 0)}`], ["client", `Phones and terminals ${num(r.kinds.client ?? 0)}`], ["guest", `Guests ${num(r.kinds.guest ?? 0)}`]].map(([k, label]) => {
        const b = h("button", { type: "button", "aria-pressed": String(ps.kind === k) }, label!) as HTMLButtonElement;
        b.addEventListener("click", () => ((ps.kind = k!), (ps.offset = 0), void loadPeers()));
        return b;
      }),
    );
    const now = Date.now() / 1000;
    peersBox.replaceChildren(
      table(
        [th("Account"), th("Device"), th("Kind"), th("Location"), th("Circuits", "num"), th("Connections", "num"), th("Connected for", "num")],
        r.peers.map((p) =>
          h(
            "tr",
            null,
            h("td", null, p.account_id ? userLink(p.account_id, who({ username: p.username, email: p.email, id: p.account_id })) : h("span", { class: "muted" }, "unknown")),
            h("td", null, p.name || "—", h("span", { class: "muted block mono" }, `${p.peer_id.slice(0, 16)}…`)),
            td(p.kind === "host" ? "computer" : p.kind === "client" ? (p.platform?.startsWith("cli") ? "terminal" : "phone") : p.kind === "guest" ? "guest" : "—"),
            geoCell(p),
            td(num(p.circuits), "num"),
            td(num(p.conns), "num"),
            td(uptime(Math.max(0, Math.round(now - p.since))), "num"),
          ),
        ),
        ps.q || ps.kind ? "Nothing matches." : "Nobody is connected to this node right now.",
      ),
      pager(r.total, r.limit, r.offset, (o) => ((ps.offset = o), void loadPeers())),
    );
  };
  search.addEventListener("input", debounce(() => ((ps.q = search.value.trim()), (ps.offset = 0), void loadPeers())));
  sortSel.addEventListener("change", () => ((ps.sort = sortSel.value), (ps.offset = 0), void loadPeers()));

  // Accounts on this node in the period, paged.
  const accBox = h("div");
  const as = { q: "", offset: 0 };
  const accSearch = h("input", { type: "search", placeholder: "Search accounts", "aria-label": "Search accounts on this node" }) as HTMLInputElement;
  const loadAcc = async () => {
    const r = await api<{ total: number; limit: number; offset: number; accounts: { account_id: string; username: string | null; email: string | null; relay_in: number; relay_out: number; days: number }[] }>(
      `${path}/accounts?${new URLSearchParams({ q: as.q, days: String(days()), limit: "50", offset: String(as.offset) })}`,
    );
    if (!r) return;
    accBox.replaceChildren(
      table(
        [th("Account"), th("Received", "num"), th("Sent", "num"), th("Active days", "num")],
        r.accounts.map((a) => h("tr", null, h("td", null, userLink(a.account_id, who({ ...a, id: a.account_id }))), td(size(a.relay_in), "num"), td(size(a.relay_out), "num"), td(num(a.days), "num"))),
        "No accounts used this node in this period.",
      ),
      pager(r.total, r.limit, r.offset, (o) => ((as.offset = o), void loadAcc())),
    );
  };
  accSearch.addEventListener("input", debounce(() => ((as.q = accSearch.value.trim()), (as.offset = 0), void loadAcc())));

  // The record, editable by admins.
  const registered = d0.node_state !== "unregistered";
  const f = {
    label: input({ value: d0.label, maxlength: "40", required: "" }),
    country: input({ value: d0.country ?? "", maxlength: "2", pattern: "([A-Za-z]{2})?", autocomplete: "off" }),
    provider: input({ value: d0.provider, maxlength: "40" }),
    model: input({ value: d0.model, maxlength: "60" }),
    monthly_gb: input({ type: "number", min: "0", max: "1000000", value: String(d0.monthly_gb) }),
    quota_count: select({}, [["out", "out only"], ["both", "in and out"]], d0.quota_count),
    billing_day: input({ type: "number", min: "1", max: "28", value: String(d0.billing_day) }),
    link_mbps: input({ type: "number", min: "0", max: "1000000", value: String(d0.link_mbps) }),
    public_ips: textarea({ class: "mono", rows: "2" }, d0.public_ips.join("\n")),
    addrs: textarea({ class: "mono", rows: "3", placeholder: "Empty: QUIC 4433 and 443 and TCP 4001 on each public IP" }, d0.addrs.map((a) => a.replace(/\/p2p\/[^/]+$/, "")).join("\n")),
    notes: textarea({ rows: "3", maxlength: "2000" }, d0.notes),
  };
  for (const el of Object.values(f)) if (!write) el.setAttribute("disabled", "");
  const lines = (t: HTMLTextAreaElement) => t.value.split(/\s+/).map((s) => s.trim()).filter(Boolean);
  const recordForm = form(
    "fields",
    async () => {
      await send("PUT", path, {
        label: f.label.value.trim(),
        country: f.country.value.trim().toUpperCase(),
        provider: f.provider.value.trim(),
        model: f.model.value.trim(),
        monthly_gb: Number(f.monthly_gb.value) || 0,
        quota_count: f.quota_count.value,
        billing_day: Number(f.billing_day.value) || 1,
        link_mbps: Number(f.link_mbps.value) || 0,
        public_ips: lines(f.public_ips),
        addrs: lines(f.addrs),
        notes: f.notes.value,
      });
      await nodePage();
      flash(`${relay} saved.`);
    },
    field("Label", f.label),
    field("Country (ISO code)", f.country),
    field("Provider", f.provider),
    field("Model", f.model),
    field("Transfer allowance, GB per month (0: none)", f.monthly_gb),
    field("Counts", f.quota_count),
    field("Billing day of the month", f.billing_day),
    field("Link, Mbit/s (0: unknown)", f.link_mbps),
    field("Public IPs, one per line", f.public_ips, true),
    field("Addresses clients dial, one per line (multiaddrs without /p2p)", f.addrs, true),
    field("Notes", f.notes, true),
    write ? h("div", null, h("button", { class: "btn small primary", type: "submit" }, "Save")) : null,
  );

  // Actions: rotation, key, enrol, retire. Secrets show once, in the box below.
  const once = h("div");
  const replaceBox = h("input", { type: "checkbox", id: "f-replace" }) as HTMLInputElement;
  const actions = h(
    "div",
    { class: "actions" },
    d0.node_state === "active"
      ? confirmButton("Take out of rotation", `Dark ${relay}: no new computers, current ones move within a minute`, async () => {
          await send("POST", `${path}/state`, { state: "dark" });
          await nodePage();
          flash(`${relay} is out of rotation.`);
        })
      : null,
    d0.node_state === "dark"
      ? actionButton(
          "Back in rotation",
          async () => {
            await send("POST", `${path}/state`, { state: "active" });
            await nodePage();
            flash(`${relay} is back in rotation.`);
          },
          "btn small primary",
        )
      : null,
    d0.node_state !== "retired"
      ? confirmButton(
          d0.key === "own" ? "Issue a new key" : "Issue key",
          d0.key === "own" ? "The current key stops working now. Sure?" : "Issue a key of its own",
          async () => {
            const r = await send<{ key: string; env_line: string; name_line: string }>("POST", `${path}/key`);
            once.replaceChildren(
              secret(
                `${relay}'s key. Shown once.`,
                [r.name_line, r.env_line],
                `Save the key to ~/.config/luwu/relay-keys/${relay}.key and run deploy/gateway/deploy.sh ${relay.toLowerCase()}. Until then ${relay} reports with the shared key, which no longer works for it.`,
              ),
            );
            flash(`A new key for ${relay} is issued. Deploy it now.`);
          },
          "btn small plain",
        )
      : null,
    d0.node_state !== "retired"
      ? actionButton(
          "Enrol command",
          async () => {
            const r = await send<{ enrol: Enrol }>("POST", `${path}/enrol-token`, { replace: replaceBox.checked });
            once.replaceChildren(
              secret(`Run this on ${relay}'s machine as root. Shown once.`, [r.enrol.command], `The token expires at ${new Date(r.enrol.expires_at * 1000).toLocaleString()}.${replaceBox.checked ? ` The machine may bring a new identity; the old peer id stops working.` : ` It must present ${relay}'s current identity; tick "replace identity" for a rebuilt machine.`}`),
            );
          },
          "btn small plain",
        )
      : null,
    d0.node_state !== "retired" ? h("label", { class: "check", for: "f-replace" }, replaceBox, " replace identity") : null,
  );
  const typed = input({ placeholder: relay, "aria-label": "Type the node name to confirm", autocomplete: "off" });
  const retireBox = h("div", { class: "twice" });
  const retire =
    isOwner() && d0.node_state !== "retired" && registered
      ? confirmButton("Retire", `Retire ${relay}: out of rotation, rules and pins. Sure?`, async () => {
          retireBox.replaceChildren(
            h("p", { class: "muted" }, "Type the node name to confirm. Its history stays; the name can't be reused until the row is removed by hand."),
            typed,
            actionButton(
              "Retire for good",
              async () => {
                if (typed.value.trim().toUpperCase() !== relay) throw new Error("The name doesn't match. Nothing was retired.");
                await send("DELETE", path);
                await nodePage();
                flash(`${relay} is retired.`);
              },
              "btn small danger armed",
            ),
          );
        })
      : null;

  const tr = d0.capacity.transfer;
  show(
    `${relay} · ${d0.label || "node"}`,
    `${d0.state === "up" ? "Up" : d0.state === "down" ? "Down" : "No reports yet"} · version ${d0.version ?? "?"} · up ${uptime(d0.uptime_s)} · last report ${ago(d0.reported_at)} · key ${d0.key}${d0.key_issued_at ? ` (issued ${date(d0.key_issued_at)})` : ""}`,
    h("div", { class: "actions" }, link("/nodes", "← All nodes"), nodeStateChip(d0), d0.enrol_pending ? h("span", { class: "muted small" }, "an enrol token is open") : null),
    !registered ? h("div", { class: "advice" }, h("p", null, `${relay} reports with the shared key but has no record. Add it on the Nodes page with the same name to manage it here.`)) : null,
    d0.warnings.length ? h("div", { class: "advice" }, h("p", null, h("b", null, `${d0.warnings.length} ${d0.warnings.length === 1 ? "warning" : "warnings"} from the gateway in the last 24 hours`)), ...d0.warnings.slice(0, 10).map((w) => h("p", null, `${dateTime(w.t)} · ${w.message}`)), d0.warnings.length > 10 ? h("p", null, link(`/logs?kind=relay.warning&relay=${encodeURIComponent(relay)}`, "all of them in the log →")) : null) : null,
    figures([
      ["Computers", num(d0.reservations), "holding a reservation"],
      ["Relayed connections", num(d0.circuits), "open now"],
      ["Devices connected", num(d0.peers)],
      ["Throughput now", rate(d0.rate_in + d0.rate_out), `↓ ${rate(d0.rate_out)} · ↑ ${rate(d0.rate_in)}`],
      ["Memory", `${d0.mem_mb} MB`],
      ["Pinned here", num(d0.pins), d0.pins ? "accounts or devices" : "no pins"],
    ]),
    h("h2", null, "Capacity ", loadChip(d0.capacity)),
    h("p", { class: d0.capacity.verdict === "full" || d0.capacity.verdict === "busy" ? "advice" : "muted" }, d0.capacity.advice),
    meters(d0.capacity),
    h("h3", null, "Transfer cycle"),
    h("div", { class: "meters" }, meter("This cycle", tr.allowance ? tr.used_pct : null, cycleLine(d0), false), meter("On pace for", tr.allowance ? tr.pace_pct : null, tr.allowance ? `${gb(tr.pace)} of ${gb(tr.allowance)} by the end of the cycle` : `${gb(tr.pace)} by the end of the cycle; no allowance set`, false)),
    write || retire ? h("h2", null, "Actions") : null,
    write ? actions : null,
    retire ? h("div", { class: "actions" }, retire) : null,
    retire ? retireBox : null,
    once,
    registered ? h("h2", null, "Record") : null,
    registered ? h("p", { class: "muted small" }, `Peer id ${d0.peer_id ?? "not enrolled yet"} · added ${d0.created_at ? date(d0.created_at) : "—"} · changed ${d0.updated_at ? ago(d0.updated_at) : "—"}`) : null,
    registered ? recordForm : null,
    h("div", { class: "listbar" }, h("h2", null, "Traffic and resources"), rangeTabs),
    chartBox,
    h("div", { class: "listbar" }, h("h2", null, "Connected now"), h("div", { class: "tools" }, search, sortSel)),
    kindBar,
    peersBox,
    h("div", { class: "listbar" }, h("h2", null, `Accounts on ${relay}, last ${days()} days`), h("div", { class: "tools" }, accSearch)),
    accBox,
  );
  drawChart(d0);
  await Promise.all([loadPeers(), loadAcc()]);
}
